/**
 * 계약서 에디터 좌표 모델 (단일 진실 출처)
 *
 * ────────────────────────────────────────────────────────────────────
 *  명목 좌표 (nominal pixel coordinates)
 * ────────────────────────────────────────────────────────────────────
 *  모든 에디터 노드(positionedText / contractField) 의 x/y/width/height 는
 *  A4 페이지 명목 픽셀(0–794 × 0–1123, 96dpi) 기준으로 저장된다.
 *
 *  - 페이지 좌상단이 (0, 0)
 *  - 페이지 우하단이 (794, 1123)
 *  - 줌·디바이스 픽셀비율과 무관 — 데이터는 항상 명목 좌표
 *
 *  화면 표시 단계에서만 PanZoomCanvas 의 transform: scale(z) 으로 시각 확대/축소.
 *  사용자 드래그/리사이즈 시 화면 픽셀 이동량을 z 로 나누어 명목 좌표 단위로
 *  되돌린 뒤 attrs 에 커밋한다.
 *
 * ────────────────────────────────────────────────────────────────────
 *  PDF 좌표와의 변환
 * ────────────────────────────────────────────────────────────────────
 *  PDF 는 1pt = 1/72 inch, 명목 dpi 96 기준 1px = 0.75pt.
 *  - to PDF (pt):    px × 0.75
 *  - from PDF (px):  pt / 0.75
 *  PDF 의 좌표계는 좌하단 원점이지만 임포트/익스포트 단계에서 좌상단 원점으로
 *  변환해 저장한다 (pdfToTiptap / PdfComposer 가 일괄 처리).
 */

/** A4 명목 크기 (96dpi) */
export const A4 = Object.freeze({
  WIDTH_PX: 794,
  HEIGHT_PX: 1123,
});

/** 페이지 여백 — 직접 작성 paragraph 의 안전 영역 */
export const PAGE_MARGIN = Object.freeze({
  TOP_PX: 75,
  BOTTOM_PX: 75,
  LEFT_PX: 75,
  RIGHT_PX: 75,
});

/** 자동 페이지네이션 트리거 — 본문 bottom 이 이 값을 넘으면 다음 페이지로 분할 */
export const PAGINATION_USABLE_BOTTOM = A4.HEIGHT_PX - PAGE_MARGIN.BOTTOM_PX;

/** PX → PT (PDF) */
export const pxToPt = (px: number): number => px * 0.75;

/** PT → PX (PDF) */
export const ptToPx = (pt: number): number => pt / 0.75;

/** 화면 클라이언트 좌표 → 페이지 명목 좌표.
 *  주어진 page DOM 요소의 boundingClientRect 와 명목 width 를 비교해 scale 을 역산하고,
 *  client 좌표를 페이지 좌상단 기준 명목 px 으로 변환한다. */
export const clientToPagePx = (
  pageEl: HTMLElement,
  clientX: number,
  clientY: number
): { x: number; y: number; scale: number } => {
  const rect = pageEl.getBoundingClientRect();
  const nominal = parseFloat(pageEl.dataset.pageWidth || `${A4.WIDTH_PX}`) || A4.WIDTH_PX;
  const scale = rect.width > 0 ? rect.width / nominal : 1;
  return {
    x: Math.max(0, Math.round((clientX - rect.left) / scale)),
    y: Math.max(0, Math.round((clientY - rect.top) / scale)),
    scale,
  };
};

/** 주어진 element 가 속한 contractPage DOM 을 거슬러 올라 찾기 */
export const closestPageEl = (el: Element | null): HTMLElement | null =>
  (el?.closest("[data-contract-page]") as HTMLElement | null) ?? null;

/** 페이지 element 의 현재 scale (Ctrl+휠 줌 반영) — 드래그 이동량 보정용 */
export const measurePageScale = (pageEl: HTMLElement): number => {
  const rect = pageEl.getBoundingClientRect();
  const nominal = parseFloat(pageEl.dataset.pageWidth || `${A4.WIDTH_PX}`) || A4.WIDTH_PX;
  return rect.width > 0 ? rect.width / nominal : 1;
};

/** 페이지 안에서 박스를 페이지 경계 내부로 클램프 */
export const clampToPage = (
  x: number,
  y: number,
  w: number,
  h: number
): { x: number; y: number } => ({
  x: Math.max(0, Math.min(x, A4.WIDTH_PX - w)),
  y: Math.max(0, Math.min(y, A4.HEIGHT_PX - h)),
});
